/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment } from 'react'
import { useNavigate } from 'react-router-dom'

import { EuiLink, EuiSpacer, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import {
  getMarketplaceBillingLink,
  getMarketplaceDisplayName,
} from '@modules/billing-lib/billingDetails/utils'
import { useBillingHistoryUrl } from '@modules/urls/billing'
import type { OrganizationResponse } from '@modules/billing-api/v1/types'

import ExternalLink from '@/components/ExternalLink'

interface Props {
  organizationId: string
  channel: OrganizationResponse['channel']
}

const MarketplacePaymentDetails = ({ organizationId, channel }: Props) => {
  const navigate = useNavigate()
  const billingHistoryUrl = useBillingHistoryUrl(organizationId)

  return (
    <Fragment>
      <EuiText size='s'>
        <FormattedMessage
          id='billing-overview.payment-details.marketplaces.details'
          defaultMessage={`Billed through your {domain} account`}
          values={{ domain: getMarketplaceDisplayName(channel) }}
        />
      </EuiText>

      <EuiSpacer size='m' />

      <EuiText size='s'>
        <FormattedMessage
          data-test-id='marketplace-account-info-text'
          id='billing-overview.payment-details.marketplaces.footer'
          defaultMessage='To make adjustments to your payment or subscription, go to your {marketplaceAccount}.'
          values={{
            marketplaceAccount: (
              <ExternalLink
                href={getMarketplaceBillingLink(channel)}
                data-test-id='marketplace-account-link'
              >
                <FormattedMessage
                  id='billing-overview.payment-details.marketplaces.marketplace-account-link'
                  defaultMessage='Marketplace account'
                />
              </ExternalLink>
            ),
          }}
        />
      </EuiText>

      <EuiSpacer size='l' />

      <EuiLink onClick={() => navigate(billingHistoryUrl)}>
        <FormattedMessage
          id='billing-overview.payment-details.view-billing-history'
          defaultMessage='View billing history'
        />
      </EuiLink>
    </Fragment>
  )
}

export default MarketplacePaymentDetails
